import type { ModelSchema } from '@arcanum-hq/shared-types';

/**
 * Anchor-resolution surface — used by Phase 3 Session 5's deep-link
 * handlers to translate URL params into concrete grid coordinates or
 * panel selectors before the parent route navigates.
 *
 * Return shape is a discriminated union: either `{ ok: true; target }`
 * carrying the resolved location, or `{ ok: false; reason }` carrying a
 * machine-readable failure code. Callers are expected to:
 *
 * 1. log the failure via `logResolutionFailure` (console + window event)
 * 2. drop the URL param so subsequent renders don't keep retrying
 * 3. NEVER surface an error modal — broken bookmarks degrade silently
 */

export type DeepLinkSurface = 'workbench' | 'signal' | 'model' | 'variables';

/**
 * Signal-tab section ids — `agent-ux-optimization` Phase 6 Session 8
 * (2026-05-08). Both `<SignalOperatingAsset>` and `<SignalNewDevelopment>`
 * wrap their rows in `<section id="signal-section-${id}">` so URL
 * `?section=…` params can scroll to the canonical anchor.
 *
 * Five canonical ids:
 *   - `hero` — title strip + 5-cell hero metrics row
 *   - `property` — 3-col property visual context grid
 *   - `story` — value-add story (3 phase cards)
 *   - `comparison` — capital stack (op-asset) OR project uses (new-dev)
 *   - `assumptions` — paginated Assumption Integrity grid
 */
export const SIGNAL_SECTION_IDS = [
  'hero',
  'property',
  'story',
  'comparison',
  'assumptions',
] as const;
export type SignalSectionId = (typeof SIGNAL_SECTION_IDS)[number];

export interface CellAnchor {
  sheet: string;
  cell: string;
  row: number;
  col: number;
}

export interface NamedRangeAnchor extends CellAnchor {
  namedRange: string;
}

export interface InputAnchor {
  namedRange: string;
}

export interface MessageAnchor {
  messageId: string;
}

export interface SearchAnchor {
  search: string;
}

/** `agent-ux-optimization` Phase 6 Session 8 — Signal section scroll. */
export interface SignalSectionAnchor {
  section: SignalSectionId;
}

/** `agent-ux-optimization` Phase 6 Session 8 — Signal assumption flash. */
export interface SignalAssumptionAnchor {
  namedRange: string;
}

export type DeepLinkParts =
  | { surface: 'model'; cell: string }
  | { surface: 'model'; namedRange: string }
  | { surface: 'model'; search: string }
  | { surface: 'workbench'; messageId: string }
  | { surface: 'workbench'; input: string }
  | { surface: 'workbench'; search: string }
  | { surface: 'signal'; messageId: string }
  | { surface: 'signal'; section: string }
  | { surface: 'signal'; assumption: string }
  | { surface: 'variables'; namedRange: string };

export type DeepLinkTarget =
  | ({ kind: 'cell' } & CellAnchor)
  | ({ kind: 'namedRange' } & NamedRangeAnchor)
  | ({ kind: 'message' } & MessageAnchor)
  | ({ kind: 'input' } & InputAnchor)
  | ({ kind: 'search' } & SearchAnchor)
  | ({ kind: 'signal-section' } & SignalSectionAnchor)
  | ({ kind: 'signal-assumption' } & SignalAssumptionAnchor);

export type DeepLinkReason =
  | 'invalid_cell_format'
  | 'unknown_named_range'
  | 'named_range_missing_location'
  | 'invalid_a1_address'
  | 'invalid_message_id'
  | 'empty_search'
  | 'unknown_section'
  | 'empty_assumption'
  | 'unknown_surface';

export type DeepLinkResolution =
  | { ok: true; target: DeepLinkTarget }
  | { ok: false; reason: DeepLinkReason; param?: string };

const A1_REGEX = /^([A-Z]+)([1-9]\d*)$/;
const SHEET_QUALIFIED_A1 = /^(.+)!([A-Z]+\d+)$/;
const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function colLettersToIndex(letters: string): number {
  let result = 0;
  for (let i = 0; i < letters.length; i++) {
    result = result * 26 + (letters.charCodeAt(i) - 64);
  }
  return result - 1;
}

function parseA1(addr: string): { row: number; col: number } | null {
  const match = A1_REGEX.exec(addr);
  if (!match) return null;
  const col = colLettersToIndex(match[1]);
  const row = parseInt(match[2], 10) - 1;
  if (!Number.isFinite(col) || !Number.isFinite(row) || row < 0 || col < 0) {
    return null;
  }
  return { row, col };
}

/**
 * Look up a named range across the model schema. Searches `inputs[]`,
 * `outputs[]`, and `intermediates[]` (when present) by exact name match.
 * Returns the first hit's `{sheet, cell}` tuple, or null if nothing
 * matches OR the matching definition lacks usable coordinates.
 */
function findNamedRangeLocation(
  name: string,
  schema: ModelSchema,
): { sheet: string; cell: string } | null {
  const collections: Array<Array<{ name: string; sheet?: string; cell?: string }>> = [
    schema.inputs,
    schema.outputs,
  ];
  for (const collection of collections) {
    for (const entry of collection) {
      if (entry.name === name && entry.sheet && entry.cell) {
        return { sheet: entry.sheet, cell: entry.cell };
      }
    }
  }
  return null;
}

/**
 * Pure resolver. Maps a `DeepLinkParts` request to a concrete target
 * (cell coordinates, message id, etc.) or a typed failure reason.
 *
 * Schema is required even for non-model surfaces because `?namedRange=`
 * resolution targeting `variables` still needs the schema's input list
 * to validate the name exists.
 */
export function resolveDeepLink(
  parts: DeepLinkParts,
  schema: ModelSchema,
): DeepLinkResolution {
  if (parts.surface === 'model') {
    if ('cell' in parts) {
      const raw = parts.cell;
      const sheetMatch = SHEET_QUALIFIED_A1.exec(raw);
      const sheet = sheetMatch ? sheetMatch[1] : '';
      const a1 = sheetMatch ? sheetMatch[2] : raw;
      if (!sheet) {
        return { ok: false, reason: 'invalid_cell_format', param: raw };
      }
      const parsed = parseA1(a1);
      if (!parsed) {
        return { ok: false, reason: 'invalid_a1_address', param: raw };
      }
      return {
        ok: true,
        target: { kind: 'cell', sheet, cell: a1, row: parsed.row, col: parsed.col },
      };
    }
    if ('namedRange' in parts) {
      const loc = findNamedRangeLocation(parts.namedRange, schema);
      if (!loc) {
        return { ok: false, reason: 'unknown_named_range', param: parts.namedRange };
      }
      const parsed = parseA1(loc.cell);
      if (!parsed) {
        return {
          ok: false,
          reason: 'named_range_missing_location',
          param: parts.namedRange,
        };
      }
      return {
        ok: true,
        target: {
          kind: 'namedRange',
          namedRange: parts.namedRange,
          sheet: loc.sheet,
          cell: loc.cell,
          row: parsed.row,
          col: parsed.col,
        },
      };
    }
    if ('search' in parts) {
      if (parts.search.trim().length === 0) {
        return { ok: false, reason: 'empty_search' };
      }
      return { ok: true, target: { kind: 'search', search: parts.search } };
    }
  }

  if (parts.surface === 'signal') {
    if ('section' in parts) {
      const sec = parts.section as SignalSectionId;
      if (!SIGNAL_SECTION_IDS.includes(sec)) {
        return { ok: false, reason: 'unknown_section', param: parts.section };
      }
      return { ok: true, target: { kind: 'signal-section', section: sec } };
    }
    if ('assumption' in parts) {
      if (typeof parts.assumption !== 'string' || parts.assumption.length === 0) {
        return { ok: false, reason: 'empty_assumption' };
      }
      return {
        ok: true,
        target: { kind: 'signal-assumption', namedRange: parts.assumption },
      };
    }
  }

  if (parts.surface === 'workbench' || parts.surface === 'signal') {
    if ('messageId' in parts) {
      if (!UUID_V4_REGEX.test(parts.messageId)) {
        return { ok: false, reason: 'invalid_message_id', param: parts.messageId };
      }
      return { ok: true, target: { kind: 'message', messageId: parts.messageId } };
    }
    if ('input' in parts && parts.surface === 'workbench') {
      return {
        ok: true,
        target: { kind: 'input', namedRange: parts.input },
      };
    }
    if ('search' in parts && parts.surface === 'workbench') {
      if (parts.search.trim().length === 0) {
        return { ok: false, reason: 'empty_search' };
      }
      return { ok: true, target: { kind: 'search', search: parts.search } };
    }
  }

  if (parts.surface === 'variables' && 'namedRange' in parts) {
    return {
      ok: true,
      target: { kind: 'input', namedRange: parts.namedRange },
    };
  }

  return { ok: false, reason: 'unknown_surface' };
}

/**
 * Side-effect logger for resolution failures. Console + window event
 * (LangSmith picks the event up downstream when wired). Never throws.
 *
 * The `arcanum:deep-link-resolution` event payload carries the surface +
 * reason + offending param so observability can aggregate failure modes
 * across users.
 */
export function logResolutionFailure(
  surface: DeepLinkSurface,
  resolution: Extract<DeepLinkResolution, { ok: false }>,
): void {
  const detail = {
    surface,
    reason: resolution.reason,
    param: resolution.param,
    deep_link_resolution_failed: true,
  };
  // eslint-disable-next-line no-console
  console.warn('[deep-link-resolution]', detail);
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(
        new CustomEvent('arcanum:deep-link-resolution', { detail }),
      );
    } catch {
      // Defensive — CustomEvent unsupported environments shouldn't crash.
    }
  }
}
